// Typed fetch wrapper for the BFF API. Auto-adds the X-Requested-With CSRF header
// (plan §10) + JSON content type, and parses the BFF's `{ok}` / `{error:{code,...}}`
// envelope. Typed against the frozen contact contract shared with the BFF
// (web/shared/contact-contract.ts via the @shared alias).
import {
  XRW_HEADER,
  XRW_VALUE,
  type ContactRequest,
  type ContactResponse,
} from "@shared/contact-contract";

export interface ApiResult<T> {
  /** HTTP status, or 0 when the request never completed (network failure). */
  status: number;
  data: T;
  /** True when fetch itself threw (offline, DNS, CORS) — no HTTP response. */
  networkError?: boolean;
}

/** Low-level JSON POST with the CSRF header attached. */
async function postJson<TReq, TRes>(path: string, body: TReq): Promise<ApiResult<TRes>> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [XRW_HEADER]: XRW_VALUE,
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Network-level failure: no HTTP response at all.
    return { status: 0, data: {} as TRes, networkError: true };
  }
  // Some responses (e.g. some 5xx) may not carry a JSON body — tolerate that.
  const data = (await res.json().catch(() => ({}))) as TRes;
  return { status: res.status, data };
}

/** Submit the business-cooperation contact form. */
export function submitContact(body: ContactRequest): Promise<ApiResult<ContactResponse>> {
  return postJson<ContactRequest, ContactResponse>("/api/contact", body);
}

/** Public runtime config served by the BFF (GET /config.json). */
export interface RuntimeConfig {
  defaultLocale: "zh" | "en";
  /** Cloudflare Turnstile site key, or null when Turnstile is disabled (v1 may launch without it). */
  turnstileSiteKey: string | null;
  publicHostname: string;
}

/**
 * Fetch the BFF runtime config. Returns null on any failure so callers can
 * degrade gracefully (e.g. render the form without Turnstile rather than break).
 */
export async function fetchRuntimeConfig(): Promise<RuntimeConfig | null> {
  try {
    const res = await fetch("/config.json", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    return (await res.json()) as RuntimeConfig;
  } catch {
    return null;
  }
}
